if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();
        this.initialized = false;
      }

      connectedCallback() {
        if (this.initialized) return;
        this.tryInitialize();
        if (!this.initialized) {
          this.observer = new MutationObserver(() => this.tryInitialize());
          this.observer.observe(this, { childList: true, subtree: true });
        }
      }

      tryInitialize() {
        this.form = this.querySelector('form');
        const variantIdInput = this.form?.querySelector('[name=id]');
        this.submitButton = this.querySelector('[type="submit"]');
        
        if (!this.form || !variantIdInput || !this.submitButton) return;

        // Remove observer after initialization
        if (this.observer) {
          this.observer.disconnect();
          this.observer = null;
        }

        variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        
        // Find cart drawer or notification
        this.cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        
        // Set aria attributes for accessibility
        if (this.cart) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';
        
        // Initialize error message elements
        this.errorMessageWrapper = this.querySelector('.product-form__error-message-wrapper');
        this.errorMessageList = this.errorMessageWrapper ? this.errorMessageWrapper.querySelector('.product-form__error-message-list') : null;

        this.setupPreorder(variantIdInput);
        this.setupRestock(variantIdInput);

        this.initialized = true;
      }

      /* ---------- Pareto Hub pre-orders ---------- */

      // snippets/buy-buttons.liquid renders a map of the variants that are on
      // pre-order ({ "<variant id>": { plan, label, date } }), a hidden
      // `selling_plan` input and a note under the button. Keep all three in
      // step with the selected variant. The `change` on the id input fires as
      // soon as the variant picker (or the sock bundle builder) sets it;
      // variantChange fires after component-variant-selects.js has reset the
      // button text to Add to cart from the re-rendered section, so the plan
      // name has to go back on then.
      setupPreorder(variantIdInput) {
        this.preorderMap = {};
        const mapScript = this.querySelector('script[data-preorder-map]');
        if (mapScript) {
          try {
            this.preorderMap = JSON.parse(mapScript.textContent) || {};
          } catch (_) {
            this.preorderMap = {};
          }
        }
        this.variantIdInput = variantIdInput;
        this.sellingPlanInput = this.form.querySelector('input[name="selling_plan"]');
        this.preorderNote = this.querySelector('[data-preorder-note]');

        variantIdInput.addEventListener('change', () => this.syncPreorder());
        // No unsubscribe on disconnect: the sock bundle builder moves this form
        // in the DOM, and connectedCallback does not run setup again.
        // PUB_SUB_EVENTS is a top-level const in constants.js, not always on window.
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          subscribe(PUB_SUB_EVENTS.variantChange, () => this.syncPreorder());
        }
        this.syncPreorder();
      }

      syncPreorder() {
        if (!this.variantIdInput) return;
        const po = this.preorderMap[String(this.variantIdInput.value)] || null;
        const wasPreorder = this.submitButton.hasAttribute('data-preorder-label');

        if (this.sellingPlanInput) {
          this.sellingPlanInput.value = po ? String(po.plan) : '';
          this.sellingPlanInput.disabled = !po;
        }
        if (this.preorderNote) this.preorderNote.hidden = !po;

        const label = this.submitButton.querySelector('span');
        if (po) {
          // assets/dynamic-add-to-cart-price.js and snippets/sleep-mask-bundle.liquid
          // read this and leave the label alone.
          this.submitButton.setAttribute('data-preorder-label', po.label);
          if (label && !this.submitButton.hasAttribute('disabled')) label.textContent = po.label;
        } else if (wasPreorder) {
          this.submitButton.removeAttribute('data-preorder-label');
          // Don't leave the old plan name on the button while the next variant's
          // section loads; component-variant-selects.js sets the final text.
          if (label && window.variantStrings) label.textContent = window.variantStrings.addToCart;
        }
      }

      /* ---------- Pareto Hub back-in-stock dates ---------- */

      // snippets/buy-buttons.liquid renders a map of the sold-out variants that
      // have stock on the way ({ "<variant id>": "21 Nov" }) and a hidden
      // "Back in stock around ..." line under the button. Show it for the
      // selected variant, on the same two signals as the pre-order label.
      setupRestock(variantIdInput) {
        this.restockMap = {};
        const mapScript = this.querySelector('script[data-restock-map]');
        if (mapScript) {
          try {
            this.restockMap = JSON.parse(mapScript.textContent) || {};
          } catch (_) {
            this.restockMap = {};
          }
        }
        this.restockNote = this.querySelector('[data-restock-note]');
        if (!this.restockNote) return;
        this.restockDate = this.restockNote.querySelector('[data-restock-date]');
        variantIdInput.addEventListener('change', () => this.syncRestock(variantIdInput));
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          subscribe(PUB_SUB_EVENTS.variantChange, () => this.syncRestock(variantIdInput));
        }
        this.syncRestock(variantIdInput);
      }

      syncRestock(variantIdInput) {
        if (!this.restockNote) return;
        const date = this.restockMap[String(variantIdInput.value)] || '';
        if (this.restockDate) this.restockDate.textContent = date;
        this.restockNote.hidden = !date;
      }

      hideErrors() {
        if (!this.errorMessageWrapper) return;
        
        this.errorMessageWrapper.toggleAttribute('hidden', true);
        if (this.errorMessageList) {
          this.errorMessageList.innerHTML = '';
        }
        this.errors = [];
        
        // Remove error styling from variant options
        this.currentProductCard = this.querySelector('[data-product-card]');
        this.currentProductCard?.querySelectorAll('[data-product-card] .variant-options').forEach((variantOption) => {
          variantOption.classList.remove('variant-options--error');
        });
      }

      onSubmitHandler(evt) {
        evt.preventDefault();

        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        if (!this.errorMessageList || this.errorMessageList.children.length === 0) {
          this.setLoadingState(true);

          const formData = new FormData(this.form);

          // Request sections inline so they reflect the updated cart state
          if (this.cart && this.cart.tagName === 'CART-DRAWER') {
            formData.append('sections', 'cart-drawer,cart-icon-bubble');
          }

          fetch(`${routes.cart_add_url}`, {
            method: 'POST',
            body: formData,
            headers: {
              'X-Requested-With': 'XMLHttpRequest',
              'Accept': 'application/json'
            }
          })
            .then(response => {
              return this.handleResponse(response);
            })
            .then((parsedState) => this.handleSuccess(parsedState))
            .catch(this.handleError.bind(this))
            .finally(() => {
              this.setLoadingState(false);
            });
        }
      }

      async handleResponse(response) {
        if (!response.ok) {
          const text = await response.text();
          throw new Error(`HTTP error! status: ${response.status} - ${text}`);
        }
        
        const contentType = response.headers.get('content-type');
        
        if (contentType && contentType.indexOf('application/json') !== -1) {
          return response.json();
        } else {
          const text = await response.text();
          throw new Error('Shopify returned non-JSON response');
        }
      }

      handleSuccess(parsedState) {
        // Check for Shopify error response (errors have status + description)
        if (parsedState.status && parsedState.description) {
          this.handleErrorMessage(parsedState.description);
          return;
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', cartData: parsedState });

        const cartDrawer = this.cart || document.querySelector('cart-drawer');
        if (cartDrawer && cartDrawer.tagName === 'CART-DRAWER') {
          this.cart = cartDrawer;

          // Use sections from the /cart/add response — guaranteed to reflect updated cart
          if (parsedState.sections) {
            cartDrawer.renderContents(parsedState);
          } else {
            // Fallback: fetch sections separately if not included in response
            Promise.all([
              fetch(routes.cart_url + '?section_id=cart-drawer').then(r => r.text()),
              fetch(routes.cart_url + '?section_id=cart-icon-bubble').then(r => r.text()),
            ])
              .then(([drawerHtml, iconHtml]) => {
                cartDrawer.renderContents({
                  sections: { 'cart-drawer': drawerHtml, 'cart-icon-bubble': iconHtml },
                });
              })
              .catch(() => {
                cartDrawer.setActiveElement(this.submitButton);
                cartDrawer.open();
              });
          }
        }
      }

      handleError(error) {
        this.handleErrorMessage(error);
      }

      setLoadingState(isLoading) {
        if (isLoading) {
          this.submitButton.setAttribute('aria-disabled', 'true');
          this.submitButton.querySelector('.loading-overlay__spinner')?.classList.remove('hidden');
        } else {
          this.submitButton.removeAttribute('aria-disabled');
          this.submitButton.querySelector('.loading-overlay__spinner')?.classList.add('hidden');
        }
      }

      handleErrorMessage(error = false) {
        this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        this.errorMessageList = this.errorMessageList || (this.errorMessageWrapper ? this.errorMessageWrapper.querySelector('.product-form__error-message-list') : null);

        if (!this.errorMessageWrapper || !this.errorMessageList) {
          return;
        }

        this.errorMessageWrapper.toggleAttribute('hidden', !error);

        if (error) {
          this.errorMessageList.innerHTML = '';
          this.errors = [];
          this.errors.push(error);
          
          this.errors.forEach((error) => {
            const errorMessage = error.message || error;
            const errorMessageText = document.createElement('p');
            errorMessageText.textContent = errorMessage;
            this.errorMessageList.appendChild(errorMessageText);
          });
        }
      }
    }
  );
}
